/**
 * TabListItem Component
 *
 * Renders a single tab item in the Recent Tabs section.
 * Handles both open and closed tabs with appropriate styling and actions.
 */

import { memo } from 'react';
import { Clock, RotateCcw } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import type { RecentTabItem } from '../utils/tabFiltering';
import { getTabTypeIcon } from '../../core/utils/tabIcon';

type TabListItemProps = {
  /** Tab item data */
  item: RecentTabItem;
  /** Whether this item is selected (for keyboard navigation) */
  isSelected?: boolean;
  /** Click handler to activate or reopen */
  onClick: () => void;
  /** Optional ref for keyboard navigation */
  itemRef?: React.RefObject<HTMLDivElement>;
};

/**
 * Format relative time (e.g., "2 min ago", "1 hour ago")
 */
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export const TabListItem = memo(function TabListItem({
  item,
  isSelected,
  onClick,
  itemRef,
}: TabListItemProps) {
  const Icon = getTabTypeIcon(item.type);

  return (
    <div
      ref={itemRef}
      role="option"
      aria-selected={isSelected}
      tabIndex={isSelected ? 0 : -1}
      className={`
        flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors
        border-b border-border/30 last:border-b-0
        ${isSelected ? 'bg-accent/50' : 'hover:bg-muted/50'}
        ${item.isOpen ? '' : 'group'}
      `}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      data-testid={`tab-list-item-${item.id}`}
    >
      {/* Icon */}
      <span
        className={`flex-shrink-0 ${item.isOpen ? 'text-primary' : 'text-muted-foreground'}`}
      >
        <Icon className="h-4 w-4" />
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{item.title}</div>
        <div className="text-xs text-muted-foreground truncate font-mono">
          {item.subtitle}
        </div>
      </div>

      {/* Connection badge (if present) */}
      {item.connectionName && (
        <span className="flex-shrink-0 text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded max-w-[100px] truncate">
          {item.connectionName}
        </span>
      )}

      {/* Status indicator / actions */}
      {item.isOpen ? (
        <span className="text-[10px] text-primary/70 font-medium flex-shrink-0">
          OPEN
        </span>
      ) : (
        <>
          {/* Time since closed */}
          {item.closedAt && (
            <span className="flex-shrink-0 text-[10px] text-muted-foreground/60 flex items-center gap-1 tabular-nums group-hover:hidden">
              <Clock className="h-2.5 w-2.5" />
              {formatRelativeTime(item.closedAt)}
            </span>
          )}
          {/* Re-open button (appears on hover) */}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className="h-6 px-2 hidden group-hover:flex items-center"
            data-testid={`reopen-tab-${item.id}`}
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Re-open
          </Button>
        </>
      )}
    </div>
  );
});
